import {
  type ObjectKind,
  type TileKind,
  type TilePos,
  type World,
  type WorldObject,
  addObject,
  canPlace,
  inBounds,
  isWalkable,
  moveObject,
  nextObjectId,
  objectAt,
  removeObject,
  setSpawn,
  setTile,
  specOf,
} from '@tama/game-core';
import type { TilePointerPhase } from '../components/WorldStage';

/**
 * Dev mode's editing rules, as a pure reducer.
 *
 * The panel owns which tool is selected and the stage reports where the pointer
 * is; everything that decides what a click *means* lives here, so the editor's
 * behaviour can be tested without a canvas or a pointer.
 */

export const DEV_TOOLS = ['wall', 'floor', 'move', 'place', 'erase', 'spawn'] as const;
export type DevTool = (typeof DEV_TOOLS)[number];

export interface DevState {
  tool: DevTool;
  /** What the `place` tool drops. */
  placeKind: ObjectKind;
  selectedId: string | null;
  /**
   * What is happening between pointer-down and pointer-up.
   *
   * Painting and dragging both need to continue across pointer moves, and both
   * need to *stop* on release — holding that in one field is what stops a
   * released drag from following the cursor around the room.
   */
  drag: DevDrag | null;
}

type DevDrag =
  | { kind: 'paint'; tile: TileKind }
  /** `dx`/`dy` are where inside the footprint the object was grabbed. */
  | { kind: 'move'; id: string; dx: number; dy: number };

export function createDevState(): DevState {
  return { tool: 'wall', placeKind: 'lamp', selectedId: null, drag: null };
}

/** The tile kind a painting tool lays down, or null for tools that do not paint. */
function paintKind(tool: DevTool): TileKind | null {
  if (tool === 'wall') return 'wall';
  if (tool === 'floor') return 'floor';
  return null;
}

export interface DevResult {
  world: World;
  dev: DevState;
}

/**
 * Applies one pointer event to the draft world.
 *
 * Returns the same objects it was given when nothing changed, so the caller's
 * "are there unsaved edits" comparison stays a cheap identity check.
 */
export function applyDevPointer(
  world: World,
  dev: DevState,
  tile: TilePos,
  phase: TilePointerPhase,
): DevResult {
  if (phase === 'up') return { world, dev: dev.drag ? { ...dev, drag: null } : dev };
  if (!inBounds(world, tile.x, tile.y)) return { world, dev };

  if (phase === 'move') return continueDrag(world, dev, tile);
  return beginAction(world, dev, tile);
}

function continueDrag(world: World, dev: DevState, tile: TilePos): DevResult {
  const drag = dev.drag;
  if (!drag) return { world, dev };

  if (drag.kind === 'paint') {
    return { world: setTile(world, tile.x, tile.y, drag.tile), dev };
  }
  return { world: moveObject(world, drag.id, tile.x - drag.dx, tile.y - drag.dy), dev };
}

function beginAction(world: World, dev: DevState, tile: TilePos): DevResult {
  const paint = paintKind(dev.tool);
  if (paint) {
    return {
      world: setTile(world, tile.x, tile.y, paint),
      dev: { ...dev, drag: { kind: 'paint', tile: paint } },
    };
  }

  switch (dev.tool) {
    case 'move': {
      const grabbed = objectAt(world, tile.x, tile.y);
      // Clicking bare floor deselects, which is the only way to put a piece of
      // furniture down without moving something else.
      if (!grabbed) return { world, dev: { ...dev, selectedId: null, drag: null } };
      return {
        world,
        dev: {
          ...dev,
          selectedId: grabbed.id,
          drag: { kind: 'move', id: grabbed.id, dx: tile.x - grabbed.x, dy: tile.y - grabbed.y },
        },
      };
    }

    case 'place': {
      const placed: WorldObject = {
        id: nextObjectId(world, dev.placeKind),
        kind: dev.placeKind,
        x: tile.x,
        y: tile.y,
      };
      const next = addObject(world, placed);
      // Selecting what was just dropped means the `move` tool picks up exactly
      // the piece the player is looking at.
      return next === world
        ? { world, dev }
        : { world: next, dev: { ...dev, selectedId: placed.id } };
    }

    case 'erase': {
      const target = objectAt(world, tile.x, tile.y);
      if (!target) return { world, dev };
      return {
        world: removeObject(world, target.id),
        dev: dev.selectedId === target.id ? { ...dev, selectedId: null } : dev,
      };
    }

    case 'spawn':
      // Refused rather than repaired: an entry point inside the wardrobe is a
      // mistake worth seeing at the moment it is made.
      if (!isWalkable(world, tile.x, tile.y)) return { world, dev };
      return { world: setSpawn(world, tile.x, tile.y), dev };

    default:
      return { world, dev };
  }
}

/**
 * Whether the current tool would do something at `tile` — the cursor's colour.
 *
 * Called from the render loop on every pointer move, so it stays a read.
 */
export function isValidTarget(world: World, dev: DevState, tile: TilePos): boolean {
  if (!inBounds(world, tile.x, tile.y)) return false;

  switch (dev.tool) {
    case 'wall':
      // Painting over the spawn point is the one edit that could shut the player
      // out of their own home, so it reads as a mistake before it is made.
      return !(tile.x === world.spawn.x && tile.y === world.spawn.y);
    case 'floor':
      return true;
    case 'move': {
      const drag = dev.drag;
      if (drag?.kind === 'move') {
        const object = world.objects.find((candidate) => candidate.id === drag.id);
        return object ? canPlace(world, object, tile.x - drag.dx, tile.y - drag.dy) : false;
      }
      return objectAt(world, tile.x, tile.y) !== null;
    }
    case 'place':
      return canPlace(world, { id: '', kind: dev.placeKind, x: tile.x, y: tile.y }, tile.x, tile.y);
    case 'erase':
      return objectAt(world, tile.x, tile.y) !== null;
    case 'spawn':
      return isWalkable(world, tile.x, tile.y);
    default:
      return false;
  }
}

/** How many tiles a piece of furniture takes up, for the tool palette's labels. */
export function describeKind(kind: ObjectKind): string {
  const spec = specOf({ id: '', kind, x: 0, y: 0 });
  return spec.width === 1 && spec.height === 1
    ? spec.label
    : `${spec.label} ${spec.width}×${spec.height}`;
}
